import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIsReducedMotionEnabled } from '@trello/a11y';
import { isDesktop } from '@trello/browser';

import { type TouchpointSourceType } from '../TouchpointSourceType';
import { trelloCrossFlowOpen } from '../TrelloCrossFlow';
import { useHasEligibleWorkspaces } from './runtimeEligibilityChecks/useHasEligibleWorkspaces';
import { useHasNoAvailableJiraSites } from './runtimeEligibilityChecks/useHasNoAvailableJiraSites';
import { useIsCrossFlowMemberConfirmed } from './runtimeEligibilityChecks/useIsCrossFlowMemberConfirmed';
import type { CrossFlowExperience } from './isSourceTargetedByExperience';
import { type CrossFlowWorkspace } from './useEligibleWorkspacesForProvisioning';
import { useIsCrossFlowExperienceEligible } from './useIsCrossFlowExperienceEligible';

interface JiraWorkItemCreationParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
  hasValidAaSession: boolean;
  workspaces: CrossFlowWorkspace[];
}

const EXPERIENCE_KEY = 'jiraWorkItemCreationExperience';

export const useCrossFlowForJiraWorkItemCreation = ({
  source,
  workspaceId,
  boardId,
  hasValidAaSession,
  workspaces,
}: JiraWorkItemCreationParams): {
  experience: CrossFlowExperience;
  isEligible: boolean;
  open: ReturnType<typeof trelloCrossFlowOpen>;
  getIsTargeted: () => boolean;
} => {
  const crossFlow = useCrossFlow();

  // 1. Check that the member is confirmed
  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();

  // 2. Check that the member has eligible workspaces
  const { isEligible: hasEligibleWorkspaces, eligibleWorkspaceOptions } =
    useHasEligibleWorkspaces({
      workspaceId,
      workspaces,
    });
  // 3. Check that the user is not on desktop
  const isWeb = !isDesktop();

  const [
    isImmediateRuntimeEligibilityChecksPassed,
    setIsImmediateRuntimeEligibilityChecksPassed,
  ] = useState(false);

  // 4. Check that the member has no available sites
  const { isEligible: hasNoAvailableSites } = useHasNoAvailableJiraSites({
    doLoadAvailableSites: isImmediateRuntimeEligibilityChecksPassed,
  });

  const experience = useIsCrossFlowExperienceEligible({
    experience: EXPERIENCE_KEY,
    source,
    eligibilityCheckResults: [
      crossFlow.isEnabled,
      // If the workspace ID is not provided, allow the user to crossflow without workspace linking
      // Overrides default behavior of checking for other eligible workspaces
      !workspaceId || hasEligibleWorkspaces,
      isMemberConfirmed,
      isWeb,
    ],
    deferredEligibilityCheckResults: [hasNoAvailableSites],
  });

  useEffect(() => {
    // TODO ENGHEALTH-2180: baselined when the rule was enabled
    // oxlint-disable-next-line react/set-state-in-effect
    setIsImmediateRuntimeEligibilityChecksPassed(
      experience.isImmediateRuntimeEligibilityChecksPassed,
    );
  }, [experience.isImmediateRuntimeEligibilityChecksPassed]);

  const getIsTargeted = useCallback((): boolean => {
    return isMemberConfirmed;
  }, [isMemberConfirmed]);

  const isReducedMotionEnabled = useIsReducedMotionEnabled();

  return useMemo(
    () => ({
      experience: experience.experience,
      isEligible: experience.isEligible,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
          isConvertingTrelloCardToJiraWorkItem: true,
          isTrelloReduceMotionEnabled: Boolean(isReducedMotionEnabled),
        },
      ),
      getIsTargeted,
    }),
    [
      experience.experience,
      experience.isEligible,
      crossFlow,
      source,
      eligibleWorkspaceOptions,
      hasValidAaSession,
      workspaceId,
      boardId,
      isReducedMotionEnabled,
      getIsTargeted,
    ],
  );
};
